import React, { useMemo, useState } from 'react';
import InventoryService from '../../../services/InventoryService';
import analytics from '../../../services/AnalyticsService';
import { toast as Toast } from '../V2Components';
import { buildAutofillSuggestions } from './scanMapping';

// Error codes the HTTP layer already surfaces globally (paywall for the scan
// quota, toast for the rate limit) — showing our own message would double up.
const GLOBALLY_HANDLED_ERRORS = new Set(['LIMIT_EXCEEDED', 'RATE_LIMIT_EXCEEDED']);

const SKIP_REASON_TEXT = {
    too_large: 'over 10 MB',
    unavailable: "couldn't be read",
};

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const scanErrorMessage = (err) => {
    const code = err?.response?.data?.error_code;
    if (code === 'NO_FILES') return 'Add a photo or document above, then try again.';
    if (code === 'NO_READABLE_FILES') return "None of this item's files could be read for scanning.";
    if (err?.response?.status === 422) {
        return "Couldn't extract any details from these files. You can still edit the fields yourself.";
    }
    return 'Scan failed. Please try again.';
};

/**
 * Smart Autofill for an existing item (edit page).
 *
 * Scans every photo and document already stored on the item, then shows the
 * changes it would make as an inline review list. Empty fields come
 * pre-selected; fields that already have a different value are listed but
 * left unticked, so nothing the user entered is replaced unless they opt in.
 * Applying only updates the form — the user still saves with Save Changes.
 *
 * Suggestions are derived from the latest `formData`, so editing a field
 * after scanning updates (or drops) its row instead of showing stale values.
 */
const ItemAutofillPanel = ({ itemId, fileCount, formData, onApply }) => {
    const [isScanning, setIsScanning] = useState(false);
    const [error, setError] = useState(null);
    // { item, skippedFiles } from the last successful scan; null when no
    // review is open.
    const [scanResult, setScanResult] = useState(null);
    // Explicit checkbox choices by suggestion id; unset rows use the default
    // (selected unless applying would replace an existing value).
    const [choices, setChoices] = useState({});

    const suggestions = useMemo(
        () => (scanResult ? buildAutofillSuggestions(formData, scanResult.item) : []),
        [formData, scanResult],
    );
    const isSelected = (s) => choices[s.id] ?? !s.replacesValue;
    const selected = suggestions.filter(isSelected);

    const handleScan = async () => {
        if (fileCount === 0 || isScanning) return;
        setIsScanning(true);
        setError(null);
        setScanResult(null);
        setChoices({});

        analytics.track('inventory.scan_initiated', { app_flow: 'edit_asset', slot_count: fileCount });
        const startedAt = now();
        try {
            const response = await InventoryService.smartScanItem(itemId);
            const durationMs = Math.round(now() - startedAt);
            const item = response?.items?.[0] || null;
            if (!item) {
                analytics.track('inventory.scan_completed', {
                    app_flow: 'edit_asset',
                    success: false,
                    duration_ms: durationMs,
                    items_extracted_count: 0,
                    error_code: 'no_items_extracted',
                });
                setError("Couldn't extract any details from these files. You can still edit the fields yourself.");
                return;
            }
            analytics.track('inventory.scan_completed', {
                app_flow: 'edit_asset',
                success: true,
                duration_ms: durationMs,
                items_extracted_count: response.items.length,
                fields_extracted_count: buildAutofillSuggestions(formData, item).length,
                item_category: item.item_category || item.category,
                item_subcategory: item.item_subcategory,
            });
            setScanResult({ item, skippedFiles: response.skipped_files || [] });
        } catch (err) {
            console.error('Item re-scan failed', err);
            const errorCode = err?.response?.data?.error_code
                || (err?.response?.status ? `http_${err.response.status}` : 'network_error');
            analytics.track('inventory.scan_completed', {
                app_flow: 'edit_asset',
                success: false,
                duration_ms: Math.round(now() - startedAt),
                items_extracted_count: 0,
                error_code: errorCode,
            });
            if (!GLOBALLY_HANDLED_ERRORS.has(err?.response?.data?.error_code)) {
                setError(scanErrorMessage(err));
            }
        } finally {
            setIsScanning(false);
        }
    };

    const closeReview = () => {
        setScanResult(null);
        setChoices({});
    };

    const handleApply = () => {
        if (selected.length === 0) return;
        onApply(selected);
        analytics.track('inventory.autofill_suggestions_applied', {
            app_flow: 'edit_asset',
            fields_extracted_count: suggestions.length,
            fields_filled_count: selected.length,
            fields_populated: selected.filter(s => s.kind === 'field').map(s => s.id),
        });
        Toast.success(`Applied ${plural(selected.length, 'change')}. Click Save Changes to keep them.`);
        closeReview();
    };

    const skippedFiles = scanResult?.skippedFiles || [];

    return (
        <div>
            <h4 className="text-lg font-bold text-[var(--primary)] font-['Manrope'] mb-1">Smart Autofill</h4>
            <p className="text-[var(--text-secondary)] text-sm leading-relaxed mb-4">
                Scan this item&apos;s photos and documents to fill in details. You&apos;ll review every change before it&apos;s applied.
            </p>

            <button
                type="button"
                onClick={handleScan}
                disabled={fileCount === 0 || isScanning}
                className="w-full py-4 bg-[var(--primary)] text-[var(--primary-text)] rounded-full font-bold text-sm hover:bg-[var(--primary-container)] transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
                {isScanning ? (
                    <>
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--primary-text)]" />
                        Scanning {plural(fileCount, 'file')}...
                    </>
                ) : (
                    `Autofill from ${plural(fileCount, 'file')}`
                )}
            </button>
            <p className="text-[10px] text-[var(--text-muted)] mt-2 text-center">
                {fileCount === 0
                    ? 'Add a photo or document above to use autofill'
                    : 'Counts as 1 scan regardless of file count'}
            </p>

            {error && (
                <p role="alert" className="text-[var(--error)] text-sm mt-4 text-center">{error}</p>
            )}

            {scanResult && (
                <section
                    aria-label="Autofill suggestions"
                    className="mt-4 border border-[var(--border)] rounded-2xl p-4 bg-[color-mix(in_srgb,var(--bg-hover),transparent_60%)]"
                >
                    {suggestions.length === 0 ? (
                        <p className="text-sm text-[var(--text-secondary)]">
                            Nothing new found — this item already has everything the scan read.
                        </p>
                    ) : (
                        <>
                            <p className="font-semibold text-sm text-[var(--primary)]">
                                Autofill found {plural(suggestions.length, 'suggestion')}
                            </p>
                            <p className="text-xs text-[var(--text-muted)] mt-1 mb-3">
                                Empty fields are pre-selected. Tick a field to replace what&apos;s there now.
                            </p>
                            <ul className="flex flex-col gap-1">
                                {suggestions.map(s => (
                                    <li key={s.id}>
                                        <label className="flex items-start gap-3 min-h-11 py-1.5 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={isSelected(s)}
                                                onChange={(e) => setChoices(prev => ({ ...prev, [s.id]: e.target.checked }))}
                                                className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--primary)] cursor-pointer"
                                            />
                                            <span className="min-w-0 flex-1">
                                                <span className="block text-sm font-semibold text-[var(--text-main)]">
                                                    {s.label}
                                                    {s.kind === 'property' && (
                                                        <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Property</span>
                                                    )}
                                                </span>
                                                <span className="block text-xs text-[var(--text-muted)] break-words">
                                                    {s.current ?? <em>Empty</em>}
                                                    {' → '}
                                                    <span className="font-semibold text-[var(--text-main)]">{s.suggested}</span>
                                                </span>
                                            </span>
                                        </label>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}

                    {skippedFiles.length > 0 && (
                        <p className="text-xs text-[var(--text-muted)] mt-3">
                            Not scanned: {skippedFiles.map(f => `${f.original_filename} (${SKIP_REASON_TEXT[f.reason] || f.reason})`).join(', ')}
                        </p>
                    )}

                    <div className="flex gap-2 mt-4">
                        <button
                            type="button"
                            onClick={closeReview}
                            className="flex-1 min-h-11 px-4 py-2 rounded-full border border-[var(--border)] text-sm font-bold text-[var(--secondary)] hover:bg-[var(--bg-hover)] transition-colors"
                        >
                            {suggestions.length === 0 ? 'Close' : 'Dismiss'}
                        </button>
                        {suggestions.length > 0 && (
                            <button
                                type="button"
                                onClick={handleApply}
                                disabled={selected.length === 0}
                                className="flex-1 min-h-11 px-4 py-2 rounded-full bg-[var(--primary)] text-[var(--primary-text)] text-sm font-bold hover:bg-[var(--primary-container)] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                Apply {selected.length}
                            </button>
                        )}
                    </div>
                </section>
            )}
        </div>
    );
};

export default ItemAutofillPanel;
